import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Package,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { BUSINESS_IMPACT_OPTIONS, TECHNOLOGY_STACKS, APPLICATION_TYPE_OPTIONS, DEVELOPMENT_PHASE_OPTIONS } from '@/helpers/collections';
import { ApplicationAPI, Application, CreateApplicationData, UpdateApplicationData } from '../api/applications';

interface ApplicationFormProps {
  isOpen: boolean;
  onClose: () => void;
  workgroupId?: number;
  application?: Application | null;
  onSuccess?: () => void;
}

export function ApplicationForm({
  isOpen,
  onClose,
  workgroupId,
  application = null,
  onSuccess
}: ApplicationFormProps) {
  const isEditMode = !!application;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    businesscriticality: '',
    applicationtype: '',
    technology: [] as string[],
    phase: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && application) {
      setFormData({
        name: application.name || '',
        description: application.description || '',
        businesscriticality: application.businesscriticality || '',
        applicationtype: application.applicationtype || '',
        technology: application.technology ? JSON.parse(application.technology) : [],
        phase: application.phase || '',
      });
      setError(null);
      setIsComplete(false);
    } else if (isOpen && !application) {
      setFormData({
        name: '',
        description: '',
        businesscriticality: '',
        applicationtype: '',
        technology: [],
        phase: '',
      });
      setError(null);
      setIsComplete(false);
    }
  }, [isOpen, application]);

  const updateFormData = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (error) {
      setError(null);
    }
  };

  const toggleArrayValue = (field: 'technology', value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(item => item !== value)
        : [...prev[field], value]
    }));
  };

  const validateForm = () => {
    return formData.name.trim() !== '' &&
      formData.businesscriticality !== '' &&
      formData.applicationtype !== '';
  };

  const handleCreateApplication = async () => {
    if (!workgroupId) {
      setError('Workgroup ID is required for creating applications');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const applicationData: CreateApplicationData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        workgroupid: workgroupId,
        businesscriticality: formData.businesscriticality,
        applicationtype: formData.applicationtype,
        technology: JSON.stringify(formData.technology),
        phase: formData.phase as 'In Development' | 'In Production' | undefined
      };

      const response = await ApplicationAPI.create(applicationData);

      if (response.data) {
        setIsSubmitting(false);
        setIsComplete(true);

        if (onSuccess) {
          onSuccess();
        }

        setTimeout(() => {
          onClose();
          setIsComplete(false);
          setFormData({
            name: '',
            description: '',
            businesscriticality: '',
            applicationtype: '',
            technology: [],
            phase: '',
          });
        }, 2000);
      } else {
        throw new Error(response.message || 'Failed to create application');
      }
    } catch (error) {
      setIsSubmitting(false);
      setError(error instanceof Error ? error.message : 'Failed to create application. Please try again.');
    }
  };

  const handleUpdateApplication = async () => {
    if (!application) {
      setError('Application data is required for updating');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const applicationData: UpdateApplicationData = {
        id: application.id,
        name: formData.name.trim(),
        workgroupid: workgroupId,
        description: formData.description.trim(),
        technology: JSON.stringify(formData.technology),
        phase: formData.phase as 'In Development' | 'In Production' | undefined,
        businesscriticality: formData.businesscriticality,
        applicationtype: formData.applicationtype,
      };

      const response = await ApplicationAPI.update(application.id, applicationData);

      if (response.data) {
        setIsSubmitting(false);
        setIsComplete(true);

        if (onSuccess) {
          onSuccess();
        }

        onClose();
        setIsComplete(false);
      } else {
        throw new Error(response.message || 'Failed to update application');
      }
    } catch (error) {
      setIsSubmitting(false);
      setError(error instanceof Error ? error.message : 'Failed to update application. Please try again.');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      if (isEditMode) {
        handleUpdateApplication();
      } else {
        handleCreateApplication();
      }
    }
  };

  const handleClose = () => {
    setError(null);
    setIsComplete(false);
    onClose();
  };

  const renderContent = () => {
    if (isComplete) {
      return (
        <div className="text-center py-8">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Application {isEditMode ? 'Updated' : 'Created'} Successfully!
          </h3>
          <p className="text-muted-foreground">
            {isEditMode
              ? `The application "${formData.name}" has been updated with your changes.`
              : `Your new application "${formData.name}" has been created and is ready for project setup.`
            }
          </p>
        </div>
      );
    }

    if (isSubmitting) {
      return (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {isEditMode ? 'Updating Application...' : 'Creating Application...'}
          </h3>
          <p className="text-muted-foreground">
            {isEditMode
              ? 'Saving your changes...'
              : 'Setting up your application and initializing security baseline...'
            }
          </p>
        </div>
      );
    }

    return (
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="name">Application Name *</Label>
          <Input
            id="name"
            type="text"
            value={formData.name}
            onChange={(e) => updateFormData('name', e.target.value)}
            placeholder="Enter application name"
            maxLength={100}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            value={formData.description}
            onChange={(e) => updateFormData('description', e.target.value)}
            placeholder="Describe the application's purpose and functionality"
            className="min-h-[100px]"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="businesscriticality">Business Criticality *</Label>
            <Select value={formData.businesscriticality} onValueChange={(value) => updateFormData('businesscriticality', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select criticality" />
              </SelectTrigger>
              <SelectContent>
                {BUSINESS_IMPACT_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="applicationtype">Application Type *</Label>
            <Select value={formData.applicationtype} onValueChange={(value) => updateFormData('applicationtype', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {APPLICATION_TYPE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="phase">Development Phase</Label>
          <Select value={formData.phase} onValueChange={(value) => updateFormData('phase', value)}>
            <SelectTrigger>
              <SelectValue placeholder="Select phase" />
            </SelectTrigger>
            <SelectContent>
              {DEVELOPMENT_PHASE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Technology Stack</Label>
          <p className="text-sm text-muted-foreground mb-3">Select technologies used in this application</p>
          <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto border rounded-lg p-3">
            {TECHNOLOGY_STACKS.map((tech) => (
              <Badge
                key={tech.value}
                variant={formData.technology.includes(tech.label) ? "default" : "outline"}
                className={`cursor-pointer transition-all ${formData.technology.includes(tech.label)
                    ? 'bg-primary text-white'
                    : 'hover:bg-gray-100'
                  }`}
                onClick={() => toggleArrayValue('technology', tech.label)}
              >
                {tech.label}
              </Badge>
            ))}
          </div>
        </div>


      </form>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Package className="w-5 h-5 text-primary" />
            <span>{isEditMode ? 'Edit Application' : 'Create New Application'}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="mt-4">
          {renderContent()}
        </div>

        {!isSubmitting && !isComplete && (
          <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              onClick={handleSubmit}
              disabled={!validateForm()}
              className="bg-primary hover:bg-primary/90"
            >
              {isEditMode ? 'Update Application' : 'Create Application'}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
